// Redux
import { Action, Result, ApiAction, AsyncResult } from '../index';
import api from '../redux-api';
import { RESET_SELECTED_CONTACT } from './common/actions';
import { savePortfolio } from './edit-portfolio';
import { deleteComparison } from './comparison';
import { startLoaderAction, stopLoaderAction } from './ui/loaders';

// Utils
import { replaceById } from '../../utils/objects';
import { PERMISSIONS, userHasPermission } from '../../utils/permissions';
import { generateLocalizedStringFromText, LOCALE } from '../../utils/i18n';
import {
  postMessageFilterContacts,
  postMessageSortContacts,
  postMessageFilterContactPortfolios,
  postMessageSortContactPortfolios,
} from '@/utils/post-messages/post-message-utils';
import localStorageUtils from '@/utils/local-storage';
import { PostMessageSender } from '@/utils/post-messages/outgoing-messages/post-message-sender';
import { sortByDateKey } from '@/utils/dates';
import { transformPortfoliosToFDC3Base, portfolioIsNew } from '@/utils/portfolios';
import { PERSISTENCE_KEYS } from '@/utils/persistence';
import { AbortControllerManager } from '@/utils/abort-controller-manager';

// Types
import { ContactsState } from './types/contacts';
import { SortingField } from '../../types/data';
import AppMode from '@/types/app-mode';
import GenericContact from '@/types/generic-contact';
import Comparison from '@/types/comparison';
import Portfolio from '@/types/portfolio';
import PortfolioHolding from '@/types/portfolio-holding';
import Activity from '@/types/activity';
import ReducedComparison from '@/types/reduced-comparison';
import ContactRiskProfile from '@/types/contact-risk-profile';
import { Dispatch } from 'redux';
import User from '@/types/user';
import { ReducedPortfolioFDC3 } from '@/types/portfolio-fdc3';
import { ReduxState } from './types';

const CONTACTS_QUERY_LIMIT = 20;
const PORTFOLIOS_QUERY_LIMIT = 20;
const COMPARISONS_QUERY_LIMIT = 20;

type GetContactsParams = {
  query?: string;
  nextToken?: string;
  sort?: SortingField[];
  withPlanId?: boolean;
  householdId?: string;
};

// Initial State
const INITIAL_STATE: ContactsState = {
  contacts: [],
  failedToFetchContacts: false,
  postMessagePortfolios: [],
  editContact: {},
  loading: {
    current: {
      portfolios: false,
      comparisons: false,
      activityLogs: false,
    },
  },
};

// Actions
const GET_MY_CONTACTS: ApiAction = {
  type: 'connect/contacts/get-my-contacts',
  endpoint: 'contacts',
};

const SET_MY_CONTACTS: Action = {
  type: 'connect/contacts/set-contacts',
};

const SET_MY_POST_MESSAGE_CONTACTS: Action = {
  type: 'connect/contacts/set-post-message-contacts',
};

export const GET_CONTACTS: Action = {
  type: 'connect/contacts/get-contacts',
};

export const GET_POST_MESSAGE_CONTACTS: Action = {
  type: 'connect/contacts/get-post-message-contacts',
};

const GET_CONTACT: ApiAction = {
  type: 'connect/contacts/get-by-id',
  endpoint: 'contacts/:id/',
};

const UPDATE_CONTACT: ApiAction = {
  type: 'connect/contacts/update',
  endpoint: 'contacts/:id/',
};

const CREATE_CONTACT: ApiAction = {
  type: 'connect/contacts/create',
  endpoint: 'contacts/',
};

const DELETE_CONTACT: ApiAction = {
  type: 'connect/contacts/delete',
  endpoint: 'contacts/:id/',
};

const DELETE_CONTACTS: Action = {
  type: 'connect/contacts/delete-many',
};

const LOAD_CONTACT: Action = {
  type: 'connect/contacts/load-edit',
};

export const SET_PERSISTENCE_SOURCE: Action = {
  type: 'connect/contacts/set-persistence-source',
};

export const GET_CONTACT_PORTFOLIOS: ApiAction = {
  type: 'connect/contacts/get-portfolios',
  endpoint: 'contacts/:id/portfolios',
};

export const ACCEPT_PORTFOLIO: ApiAction = {
  type: 'connect/portfolios/accept',
  endpoint: 'contacts/:id/portfolios/:portfolioId/accept',
};

export const SET_CONTACT_PORTFOLIOS: Action = {
  type: 'connect/contacts/set-portfolios',
};

const GET_CONTACT_COMPARISONS: ApiAction = {
  type: 'connect/contacts/get-comparisons',
  endpoint: 'contacts/:id/comparisons',
};

const GET_CONTACT_ACTIVITY: ApiAction = {
  type: 'connect/contacts/get-contact-activity',
  endpoint: 'users/:id/activity-logs',
};

const SET_SELECTED_CONTACT: Action = {
  type: 'connect/contacts/set-selected',
};

export const REPLACE_CONTACT_PORTFOLIO: Action = {
  type: 'connect/contacts/replace-contact-portfolio',
};

export const DELETE_CONTACT_PORTFOLIOS: ApiAction = {
  type: 'connect/contacts/delete-contact-portfolio-many',
  endpoint: 'contacts/:id/portfolios',
};

const DELETE_CONTACT_COMPARISON: Action = {
  type: 'connect/contacts/delete-contact-comparison',
};

const DELETE_CONTACT_COMPARISONS: Action = {
  type: 'connect/contacts/delete-contact-comparisons',
};

const REMOVE_PORTFOLIO_PLAN_ACCOUNT: Action = {
  type: 'connect/contacts/remove-portfolio-plan-account',
};

const RESET_EDIT_CONTACT: Action = {
  type: 'connect/contacts/reset-edit',
};

export const ADD_CONTACT_PORTFOLIO: Action = {
  type: 'connect/contacts/add-portfolio',
};

const INIT_LOADING_PORTFOLIOS: Action = {
  type: 'connect/contacts/init-loading-portfolios',
};

const INIT_LOADING_COMPARISONS: Action = {
  type: 'connect/contacts/init-loading-comparisons',
};

const SAVE_CONTACT_RISK_PROFILE: ApiAction = {
  type: 'connect/contacts/risk-profile/save-risk-profile',
  endpoint: 'contacts/:contactId/risk-profiles',
};

const DELETE_CONTACT_RISK_PROFILE: ApiAction = {
  type: 'connect/contacts/risk-profile/delete-risk-profile',
  endpoint: 'contacts/:contactId/risk-profiles/:profileId/',
};

const UPDATE_CONTACT_RISK_PROFILE: ApiAction = {
  type: 'connect/contacts/risk-profile/update-risk-profile',
  endpoint: 'contacts/:contactId/risk-profiles/:profileId/',
};

const RESET_NEXT_CONTACT_TOKEN: Action = {
  type: 'connect/contacts/reset-next-contacts-token',
};

const RESET_NEXT_CURRENT_CONTACT_PORTFOLIOS_TOKEN: Action = {
  type: 'connect/contacts/reset-next-current-contact-portfolios-token',
};

export const SET_IS_SAVING_CONTACT_RISK_PROFILE: Action = {
  type: 'connect/contacts/risk-profile/set-is-saving-contact-risk-profile',
};

export const UPDATE_CONTACT_COMPARISON: Action = {
  type: 'connect/contacts/comparisons/update',
};

export const ADD_NEWLY_CREATED_PORTFOLIO_ID: Action = {
  type: 'connect/contacts/add-newly-created-portfolio-id',
};

export const CLEAR_NEWLY_CREATED_PORTFOLIO_IDS: Action = {
  type: 'connect/contacts/clear-newly-created-portfolio-ids',
};

// AbortController manager for cancelling in-flight requests
// Stored outside Redux to avoid serialization issues
const abortManager = new AbortControllerManager([GET_CONTACT_PORTFOLIOS.type]);

// Reducer
export default function reducer(state: ContactsState = INITIAL_STATE, action: Action) {
  switch (action.type) {
    case SET_MY_CONTACTS.type: {
      return {
        ...state,
        contacts: action.contacts,
      };
    }
    case SET_MY_POST_MESSAGE_CONTACTS.type: {
      return {
        ...state,
        postMessageContacts: action.postMessageContacts,
      };
    }
    case SET_CONTACT_PORTFOLIOS.type: {
      return {
        ...state,
        postMessagePortfolios: action.portfolios,
      };
    }
    case `${GET_MY_CONTACTS.type}_SUCCESS`: {
      return {
        ...state,
        failedToFetchContacts: false,
        contacts: action.params.next ? state.contacts.concat(action.data.data.contacts) : action.data.data.contacts,
        nextContactsToken: action.data.data.next,
      };
    }
    case `${GET_MY_CONTACTS.type}_ERROR`: {
      return {
        ...state,
        failedToFetchContacts: true,
        nextContactsToken: undefined,
      };
    }
    case `${DELETE_CONTACTS.type}_SUCCESS`: {
      return {
        ...state,
        contacts: state.contacts.filter(c => !action.ids.includes(c._id)),
      };
    }
    case `${DELETE_CONTACT.type}_SUCCESS`: {
      return {
        ...state,
        contacts: state.contacts.filter(c => c._id !== action.data.contact._id),
      };
    }
    case `${UPDATE_CONTACT.type}_SUCCESS`: {
      const newState: ContactsState = {
        ...state,
        contacts: replaceById(state.contacts.slice(), action.data.contact, '_id'),
      };

      // If the saved contact is loaded as the current contact, replace it.
      if (state.current !== undefined && state.current.contact._id === action.data.contact._id) {
        newState.current = {
          ...state.current,
          contact: action.data.contact,
        };
      }

      return newState;
    }
    case `${CREATE_CONTACT.type}_SUCCESS`: {
      return {
        ...state,
        contacts: state.contacts.concat([action.data.contact]),
      };
    }
    case LOAD_CONTACT.type: {
      return {
        ...state,
        editContact: action.contact,
      };
    }
    case SET_SELECTED_CONTACT.type: {
      return {
        ...state,
        current: {
          contact: action.contact,
          portfolios: [],
          comparisons: [],
          riskProfiles: action.contact.riskProfiles?.slice().sort(sortByDateKey) || [],
          hasMore: {
            portfolios: true,
            comparisons: true,
          },
        },
      };
    }
    case GET_CONTACT_COMPARISONS.type: {
      return {
        ...state,
        loading: {
          ...state.loading,
          current: {
            ...state.loading.current,
            comparisons: true,
          },
        },
      };
    }
    case `${GET_CONTACT_COMPARISONS.type}_ERROR`: {
      if (!state.current) {
        return state;
      }

      return {
        ...state,
        current: {
          ...state.current,
          comparisons: [],
          hasMore: {
            ...state.current.hasMore,
            comparisons: false,
          },
        },
        loading: {
          ...state.loading,
          current: {
            ...state.loading.current,
            comparisons: false,
          },
        },
      };
    }
    case `${GET_CONTACT_COMPARISONS.type}_SUCCESS`: {
      if (!state.current) {
        return state;
      }

      const { comparisons, next } = action.data.data;

      return {
        ...state,
        current: {
          ...state.current,
          nextContactComparisonsToken: next,
          comparisons: action.params.next && state.current.comparisons ? state.current.comparisons.concat(comparisons) : comparisons,
          hasMore: {
            ...state.current.hasMore,
            comparisons: !!next,
          },
        },
        loading: {
          ...state.loading,
          current: {
            ...state.loading.current,
            comparisons: false,
          },
        },
      };
    }
    case SET_PERSISTENCE_SOURCE.type: {
      return {
        ...state,
        persistenceSource: action.source,
      };
    }
    case GET_CONTACT_PORTFOLIOS.type: {
      return {
        ...state,
        loading: {
          ...state.loading,
          current: {
            ...state.loading.current,
            portfolios: !action.next,
          },
        },
      };
    }
    case `${GET_CONTACT_PORTFOLIOS.type}_ERROR`: {
      if (!state.current) {
        return state;
      }

      return {
        ...state,
        current: {
          ...state.current,
          portfolios: [],
          hasMore: {
            ...state.current.hasMore,
            portfolios: false,
          },
        },
        loading: {
          ...state.loading,
          current: {
            ...state.loading.current,
            portfolios: false,
          },
        },
      };
    }
    case `${GET_CONTACT_PORTFOLIOS.type}_SUCCESS`: {
      if (!state.current) {
        return state;
      }
      const { portfolios, next } = action.data.data;

      return {
        ...state,
        current: {
          ...state.current,
          portfolios: action.params.next && state.current.portfolios ? state.current.portfolios.concat(portfolios) : portfolios,
          nextContactPortfoliosToken: next,
          hasMore: {
            ...state.current.hasMore,
            portfolios: !!next,
          },
        },
        loading: {
          ...state.loading,
          current: {
            ...state.loading.current,
            portfolios: false,
          },
        },
      };
    }
    case GET_CONTACT_ACTIVITY.type: {
      return {
        ...state,
        loading: {
          ...state.loading,
          current: {
            ...state.loading.current,
            activityLogs: true,
          },
        },
      };
    }
    case `${GET_CONTACT_ACTIVITY.type}_SUCCESS`: {
      if (!state.current) {
        return state;
      }

      const { logs, next } = action.data.data;

      return {
        ...state,
        current: {
          ...state.current,
          activityLogs: action.params.next && state.current && state.current.activityLogs ? state.current.activityLogs.concat(logs) : logs,
          nextActivityLogToken: next,
        },
        loading: {
          ...state.loading,
          current: {
            ...state.loading.current,
            activityLogs: false,
          },
        },
      };
    }
    case `${GET_CONTACT_ACTIVITY.type}_ERROR`: {
      return {
        ...state,
        loading: {
          ...state.loading,
          current: {
            ...state.loading.current,
            activityLogs: false,
          },
        },
      };
    }
    case RESET_SELECTED_CONTACT.type: {
      return {
        ...state,
        current: undefined,
      };
    }
    case ADD_CONTACT_PORTFOLIO.type: {
      if (!state.current || !state.current.portfolios) {
        return state;
      }

      return {
        ...state,
        current: {
          ...state.current,
          portfolios: [action.portfolio].concat(state.current.portfolios),
        },
      };
    }
    case REPLACE_CONTACT_PORTFOLIO.type: {
      if (!state.current || !state.current.portfolios) {
        return state;
      }

      return {
        ...state,
        current: {
          ...state.current,
          portfolios: replaceById(state.current.portfolios.slice(), action.portfolio, '_id', true),
        },
      };
    }
    case `${DELETE_CONTACT_PORTFOLIOS.type}_SUCCESS`: {
      if (!state.current || !state.current.portfolios) {
        return state;
      }
      const ids = action.data.portfolioIds;
      return {
        ...state,
        current: {
          ...state.current,
          portfolios: state.current.portfolios.filter(p => !ids.includes(p._id)),
        },
      };
    }
    case DELETE_CONTACT_COMPARISON.type: {
      if (!state.current || !state.current.comparisons) {
        return state;
      }

      return {
        ...state,
        current: {
          ...state.current,
          comparisons: state.current.comparisons.filter(comparison => comparison._id !== action.comparison._id),
        },
      };
    }
    case DELETE_CONTACT_COMPARISONS.type: {
      if (!state.current || !state.current.comparisons) {
        return state;
      }
      return {
        ...state,
        current: {
          ...state.current,
          comparisons: state.current.comparisons.filter(comparison => !action.comparisons.flatMap((c: ReducedComparison) => c._id).includes(comparison._id)),
        },
      };
    }
    case REMOVE_PORTFOLIO_PLAN_ACCOUNT.type: {
      if (!state.current?.portfolios) {
        return state;
      }

      return {
        ...state,
        current: {
          ...state.current,
          portfolios: state.current.portfolios.map(portfolio => {
            if (portfolio._id === action.portfolioId) {
              return {
                ...portfolio,
                planning: {},
                riskProfile: undefined,
              };
            }
            return portfolio;
          }),
        },
      };
    }
    case RESET_EDIT_CONTACT.type: {
      return {
        ...state,
        editContact: {},
      };
    }
    case INIT_LOADING_PORTFOLIOS.type: {
      if (!state.current) {
        return state;
      }

      return {
        ...state,
        current: {
          ...state.current,
          portfolios: undefined,
        },
        loading: {
          ...state.loading,
          current: {
            ...state.loading.current,
            portfolios: true,
          },
        },
      };
    }
    case INIT_LOADING_COMPARISONS.type: {
      if (!state.current) {
        return state;
      }

      return {
        ...state,
        current: {
          ...state.current,
          comparisons: undefined,
        },
        loading: {
          ...state.loading,
          current: {
            ...state.loading.current,
            comparisons: true,
          },
        },
      };
    }
    case `${SAVE_CONTACT_RISK_PROFILE.type}_SUCCESS`: {
      if (!state.current) {
        return state;
      }

      const newRiskProfile = action.data.data;
      const riskProfiles = state.current.contact.riskProfiles?.slice() || [];

      if (riskProfiles) {
        riskProfiles.push(newRiskProfile);
        riskProfiles.sort(sortByDateKey);
      }

      return {
        ...state,
        current: {
          ...state.current,
          contact: {
            ...state.current.contact,
            riskProfiles,
          },
        },
      };
    }
    case `${UPDATE_CONTACT_RISK_PROFILE.type}_SUCCESS`: {
      if (!state.current) {
        return state;
      }

      const updatedRiskProfile = action.data.data;
      const newList = replaceById(state.current.contact.riskProfiles?.slice() || [], updatedRiskProfile, '_id');
      const riskProfiles = newList.sort(sortByDateKey);

      return {
        ...state,
        current: {
          ...state.current,
          contact: {
            ...state.current.contact,
            riskProfiles,
          },
        },
      };
    }
    case `${DELETE_CONTACT_RISK_PROFILE.type}_SUCCESS`: {
      if (!state.current || !state.current.contact.riskProfiles) {
        return state;
      }

      return {
        ...state,
        current: {
          ...state.current,
          contact: {
            ...state.current.contact,
            riskProfiles: state.current.contact.riskProfiles.filter(profile => profile._id !== action.params.profileId),
          },
        },
      };
    }

    case RESET_NEXT_CONTACT_TOKEN.type: {
      return {
        ...state,
        nextContactsToken: undefined,
      };
    }

    case RESET_NEXT_CURRENT_CONTACT_PORTFOLIOS_TOKEN.type: {
      if (!state.current) {
        return state;
      }

      return {
        ...state,
        current: {
          ...state.current,
          nextContactPortfoliosToken: undefined,
        },
      };
    }

    case UPDATE_CONTACT_COMPARISON.type: {
      const { comparison } = action;

      if (!state.current?.comparisons?.length) {
        return state;
      }

      return {
        ...state,
        current: {
          ...state.current,
          comparisons: state.current.comparisons.map(c => (c._id === comparison._id ? comparison : c)),
        },
      };
    }

    case ADD_NEWLY_CREATED_PORTFOLIO_ID.type: {
      if (!state.current) {
        return state;
      }

      const currentIds = state.current.newlyCreatedPortfolioIds || [];
      return {
        ...state,
        current: {
          ...state.current,
          newlyCreatedPortfolioIds: [...currentIds, action.portfolioId],
        },
      };
    }

    case CLEAR_NEWLY_CREATED_PORTFOLIO_IDS.type: {
      if (!state.current) {
        return state;
      }

      return {
        ...state,
        current: {
          ...state.current,
          newlyCreatedPortfolioIds: undefined,
        },
      };
    }

    default: {
      return state;
    }
  }
}

// Action Creators
export const setMyContacts =
  (contacts: GenericContact[]): Result<void> =>
  dispatch => {
    // Contacts list used to update the components
    dispatch({ type: SET_MY_CONTACTS.type, contacts });

    // Original contacts list from the Post Message used as back up when Filtering / Sorting
    dispatch({ type: SET_MY_POST_MESSAGE_CONTACTS.type, postMessageContacts: contacts });

    dispatch(stopLoaderAction(GET_POST_MESSAGE_CONTACTS.type));
  };

export const setMyPortfolios =
  (portfolios: Portfolio[]): Result<void> =>
  dispatch => {
    dispatch({ type: SET_CONTACT_PORTFOLIOS.type, portfolios });
  };

export const getContactsFromCapIntel = (
  dispatch: Dispatch,
  getState: Function,
  query?: string,
  nextToken?: string,
  sort?: SortingField[],
  withPlanId?: boolean,
  householdId?: string,
): Promise<GenericContact[]> => {
  if (nextToken == null) {
    dispatch(startLoaderAction(GET_CONTACTS.type));
  }

  type GetAPIContactsParams = {
    query?: string;
    next?: string;
    limit?: number;
    sort?: string;
    persistenceKey?: string;
    withPlanId?: boolean;
    householdId?: string;
  };

  const state = getState();
  const {
    auth: { user },
    contacts: { current },
    households: { currentHousehold },
  } = state;

  const params: GetAPIContactsParams = {
    limit: CONTACTS_QUERY_LIMIT,
  };

  if (query) {
    params.query = query;
  }

  if (nextToken != null) {
    params.next = nextToken;
  } else {
    dispatch(RESET_NEXT_CONTACT_TOKEN);
  }

  if (sort && sort.length > 0) {
    params.sort = `${sort[0].id} ${sort[0].order}`;
  }

  if (withPlanId) {
    params.withPlanId = true;
  }

  const householdIdFromParams = householdId || currentHousehold?.id;
  if (householdIdFromParams) {
    params.householdId = householdIdFromParams;
  }

  const persistenceSource = localStorageUtils.getPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE);
  if (persistenceSource) {
    params.persistenceKey = persistenceSource;
  }

  if (user == null || (user.appConfig.mode === AppMode.CONTACT && !householdIdFromParams)) {
    if (current) {
      const contacts: GenericContact[] = [current.contact];
      if (current.contact.linkedContacts != null) {
        contacts.push(...current.contact.linkedContacts);
      }
      dispatch({ type: `${GET_MY_CONTACTS.type}_SUCCESS`, data: { data: { contacts } }, params: {} });
    }
    return Promise.resolve([]).finally(() => {
      dispatch(stopLoaderAction(GET_CONTACTS.type));
    });
  }

  return api
    .get(GET_MY_CONTACTS, params)
    .then((res: { data: any }) => res.data)
    .finally(() => {
      dispatch(stopLoaderAction(GET_CONTACTS.type));
    });
};

export const getContactsFromPostMessage = async (dispatch: Dispatch, getState: Function, query?: string, sort?: SortingField[]): Promise<GenericContact[]> => {
  const state = getState();
  const {
    contacts: { postMessageContacts },
  } = state;
  // Reset the contacts list from the Original Post Message contacts list
  let filteredContacts: GenericContact[] = postMessageContacts;

  if (query) {
    filteredContacts = postMessageFilterContacts(filteredContacts, query);
  }

  filteredContacts = postMessageSortContacts(filteredContacts, sort);

  return Promise.resolve(filteredContacts).finally(() => {
    dispatch({ type: SET_MY_CONTACTS.type, contacts: filteredContacts });
    dispatch(stopLoaderAction(GET_CONTACTS.type));
  });
};

export const getMyContacts =
  (options?: GetContactsParams): AsyncResult<GenericContact[]> =>
  (dispatch, getState) => {
    const { isPostMessageActive, postMessageActivation } = getState().postMessage;
    const { query, nextToken, sort, withPlanId, householdId } = options || {};

    return isPostMessageActive && postMessageActivation.contact
      ? getContactsFromPostMessage(dispatch, getState, query, sort)
      : getContactsFromCapIntel(dispatch, getState, query, nextToken, sort, withPlanId, householdId);
  };

export const getContactByIdFromCapIntel = (id: string): Promise<GenericContact> => api.get(GET_CONTACT, { id }).then((res: { data: any }) => res.data);

export const getContactByIdFromPostMessage = (getState: Function, id: string): Promise<GenericContact> => {
  const state = getState();
  const {
    contacts: { contacts },
  } = state;

  // Get the contact from the store
  const selectedContact = contacts.find((contact: GenericContact) => contact._id === id);
  return Promise.resolve(selectedContact);
};

export const getContactById =
  (id: string): AsyncResult<GenericContact> =>
  (dispatch, getState) => {
    const { isPostMessageActive } = getState().postMessage;
    return isPostMessageActive ? getContactByIdFromPostMessage(getState, id) : getContactByIdFromCapIntel(id);
  };

export const saveContact =
  (contact: Partial<GenericContact>): AsyncResult<GenericContact> =>
  () => {
    if (contact._id) {
      return api.put(UPDATE_CONTACT, { id: contact._id }, contact).then((res: { contact: any }) => res.contact);
    }

    return api.post(CREATE_CONTACT, null, contact).then((res: { contact: any }) => res.contact);
  };

export const deleteContacts = (contacts: GenericContact[]): AsyncResult<void> => {
  type deleteResult = { id: string };

  return dispatch => {
    const promises: Promise<deleteResult>[] = contacts.map(c => api.delete(DELETE_CONTACT, { id: c._id }));
    return Promise.all(promises).then((results: deleteResult[]) => {
      const ids = results.reduce((acc: string[], r) => {
        acc.push(r.id);
        return acc;
      }, []);
      dispatch({ type: `${DELETE_CONTACTS.type}_SUCCESS`, ids });
    });
  };
};

export const deleteContact =
  (contact: GenericContact): AsyncResult<void> =>
  () =>
    api.delete(DELETE_CONTACT, { id: contact._id });

export const loadContactForEdit =
  (contact: GenericContact): Result<void> =>
  dispatch => {
    dispatch({ type: LOAD_CONTACT.type, contact });
  };

export const getContactComparisons =
  (contact: GenericContact, query?: string): AsyncResult<Comparison[]> =>
  (dispatch, getState) => {
    const { current } = getState().contacts;

    if (!current || !current.contact) {
      return Promise.reject();
    }

    const params: any = {
      id: contact._id,
      limit: COMPARISONS_QUERY_LIMIT,
    };

    if (query) {
      params.query = query;
    }

    if (current.nextContactComparisonsToken) {
      params.next = current.nextContactComparisonsToken;
    }

    return api.get(GET_CONTACT_COMPARISONS, params).then((res: { data: any }) => res.data);
  };

export const getContactActivity =
  (contact: GenericContact, nextToken?: string): AsyncResult<Activity[]> =>
  () => {
    const params: any = {
      id: contact.user,
    };

    if (nextToken) {
      params.next = nextToken;
    }

    return api.get(GET_CONTACT_ACTIVITY, params);
  };

const getPersistenceKey = (user: User) => {
  // We only persist external portfolios on external clients, so avoid using the persistenceKey if we are
  // dealing with local contacts unless we do not use PERSISTENCE_KEYS.EXISTING_CLIENT_PORTFOLIO
  const hasPersistenceKey = user?.appConfig.persistenceKeys?.includes(PERSISTENCE_KEYS.EXISTING_CLIENT_CONTACT);
  if (!hasPersistenceKey || localStorageUtils.getPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE)) {
    const persistenceSource = localStorageUtils.getPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PORTFOLIO_PERSISTENCE_SOURCE);
    if (persistenceSource) {
      return persistenceSource;
    }
  }

  return null;
};

const setExternalOnPortfoliosHoldings = (portfolios: Portfolio[]) => {
  portfolios.forEach((portfolio: Portfolio) => {
    portfolio.holdings.forEach((holding: PortfolioHolding) => {
      holding.external = true;
    });
  });
};

export const getContactPortfoliosFromCapIntel = (
  dispatch: Dispatch,
  getState: Function,
  query?: string,
  sort?: SortingField[],
  shouldFetchNext = true,
): Promise<Portfolio[]> => {
  const state = getState();
  const {
    auth: { user },
    contacts: { current },
  } = state;
  let {
    intl: { locale },
  } = state;

  if (!current || !current.contact) {
    return Promise.reject();
  }

  // TODO - Support en-US filters [CON-8332]
  if (locale === LOCALE.EN_US) {
    locale = LOCALE.EN_CA;
  }

  const params: any = {
    id: current.contact._id,
    locale,
  };

  if (query) {
    params.query = query;
  }

  if (sort && sort.length > 0) {
    params.sort = `${sort[0].id} ${sort[0].order}`;
  }

  if (shouldFetchNext && current.nextContactPortfoliosToken) {
    params.next = current.nextContactPortfoliosToken;
    params.limit = PORTFOLIOS_QUERY_LIMIT;
  }

  const persistenceKey = getPersistenceKey(user);
  if (persistenceKey) {
    params.persistenceKey = persistenceKey;
  }

  const action = { ...GET_CONTACT_PORTFOLIOS };
  if (persistenceKey) {
    action.transform = (response: { data: { portfolios: Portfolio[] } }) => {
      setExternalOnPortfoliosHoldings(response.data.portfolios);

      return { data: response.data };
    };
  }

  const controller = abortManager.getController(GET_CONTACT_PORTFOLIOS.type);

  return api
    .get(action, params, { signal: controller.signal })
    .then((res: { data: { portfolios: Portfolio[] } }) => {
      const { portfolios } = res.data;
      return portfolios;
    })
    .finally(() => {
      abortManager.clear(GET_CONTACT_PORTFOLIOS.type);
    });
};

export const getContactPortfoliosFromPostMessage = (
  dispatch: Dispatch,
  getState: () => ReduxState,
  query?: string,
  sort?: SortingField[],
): Promise<Portfolio[]> => {
  const state = getState();
  let {
    contacts: { current, postMessagePortfolios },
    intl: { locale },
    auth: { user },
  } = state;

  if (locale === LOCALE.EN_US) {
    locale = LOCALE.EN_CA;
  }

  const currentContactId = current?.contact._id;
  const currentContactPortfolios = currentContactId ? postMessagePortfolios.filter((portfolio: Portfolio) => portfolio.contact?._id === currentContactId) : [];

  const portfoliosWithNoHoldings = currentContactPortfolios.filter((portfolio: Portfolio) => portfolio.holdings.length === 0);
  // if portfolios have no holdings, we need to fetch them from the post message event
  if (portfoliosWithNoHoldings.length > 0) {
    dispatch(INIT_LOADING_PORTFOLIOS);
    const selectedFDC3BasePortfolios: ReducedPortfolioFDC3[] = transformPortfoliosToFDC3Base(portfoliosWithNoHoldings);
    PostMessageSender.getPortfolios(dispatch, getState, user, selectedFDC3BasePortfolios);
    // No need to return to portfolio here since they will be added to the store in the post message portfolios-strategy
    return Promise.resolve([]);
  }
  // If all portfolios have holdings, we can filter and sort them directly
  let filteredContactPortfolios: Portfolio[] = currentContactPortfolios;

  if (query) {
    filteredContactPortfolios = postMessageFilterContactPortfolios(locale, filteredContactPortfolios, query);
  }

  filteredContactPortfolios = postMessageSortContactPortfolios(locale, filteredContactPortfolios, sort);

  setExternalOnPortfoliosHoldings(filteredContactPortfolios);

  return Promise.resolve(filteredContactPortfolios).finally(() => {
    // Format the success data to seamlessly integrate the Post Message flow
    const dispatchSuccessData = {
      data: {
        ...current,
        hasMore: {
          ...current?.hasMore,
          portfolios: false,
        },
        portfolios: filteredContactPortfolios,
      },
    };
    dispatch({ type: `${GET_CONTACT_PORTFOLIOS.type}_SUCCESS`, data: dispatchSuccessData, params: {} });
  });
};

export const getContactPortfolios =
  (query?: string, sort?: SortingField[], shouldFetchNext = true): AsyncResult<Portfolio[]> =>
  (dispatch, getState) => {
    const { isPostMessageActive, postMessageActivation } = getState().postMessage;
    return isPostMessageActive && postMessageActivation.portfolio
      ? getContactPortfoliosFromPostMessage(dispatch, getState, query, sort)
      : getContactPortfoliosFromCapIntel(dispatch, getState, query, sort, shouldFetchNext);
  };

export const loadFactsetAccountLookup = (): Result<void> => (dispatch, getState) => {
  const {
    auth: { user },
  } = getState();
  PostMessageSender.openAccountLookup(user, getState);
};

export const loadContactData =
  (contact: GenericContact): AsyncResult<void> =>
  (dispatch, getState) => {
    dispatch({ type: SET_SELECTED_CONTACT.type, contact });

    const {
      auth: { user },
    } = getState();

    if (!user) {
      return Promise.reject();
    }

    const activityPromise: Promise<Activity[]>[] = [];

    if (contact.user != null && userHasPermission(user, PERMISSIONS.ACTIVITY_LOGS)) {
      activityPromise.push(dispatch(getContactActivity(contact)));
    }

    return Promise.all([dispatch(getContactComparisons(contact)), dispatch(getContactPortfolios()), activityPromise]).then(() => {});
  };

export const loadContactPortfolios =
  (contact: GenericContact): AsyncResult<void> =>
  dispatch => {
    dispatch({ type: SET_SELECTED_CONTACT.type, contact });
    return dispatch(getContactPortfolios()).then(() => {});
  };

export const deleteContactPortfolios =
  (portfolios: Portfolio[], contactId: string): AsyncResult<{ portfolioIds: string[] }> =>
  () => {
    if (portfolios.length === 0) {
      return Promise.reject();
    }

    const params = {
      id: contactId,
      portfolioIds: portfolios.map(portfolio => portfolio._id).join(';'),
    };

    return api.delete(DELETE_CONTACT_PORTFOLIOS, params);
  };

export const deleteContactComparison =
  (comparison: ReducedComparison): AsyncResult<void> =>
  dispatch =>
    dispatch(deleteComparison(comparison)).then(() => {
      dispatch({ type: DELETE_CONTACT_COMPARISON.type, comparison });
    });

export const removeContactComparisonsFromCurrent =
  (comparisons: ReducedComparison[]): Result<void> =>
  dispatch => {
    dispatch({ type: DELETE_CONTACT_COMPARISONS.type, comparisons });
  };

export const removePlanAccountFromPortfolio =
  (portfolio: Portfolio): Result<Promise<Portfolio>> =>
  dispatch => {
    dispatch({
      type: REMOVE_PORTFOLIO_PLAN_ACCOUNT.type,
      portfolioId: portfolio._id,
    });

    const updatedPortfolio = {
      ...portfolio,
      riskProfile: undefined,
      planning: {
        planId: '',
        planAccountId: '',
      },
    };

    return dispatch(savePortfolio(updatedPortfolio));
  };

export const saveContactPortfolio =
  (portfolio: Portfolio): AsyncResult<Portfolio> =>
  async dispatch => {
    const planAccount = portfolio.planning?.planAccount;
    const savedPortfolio = await dispatch(savePortfolio(portfolio));
    // Put the plan account back into the portfolio (POST call will not return it in the saved portfolio)
    const savedPortfolioWithPlanAccount = {
      ...savedPortfolio,
      ...(savedPortfolio.planning && planAccount && { planning: { planAccount, ...savedPortfolio.planning } }),
    };

    const actionType = portfolio._id ? REPLACE_CONTACT_PORTFOLIO.type : ADD_CONTACT_PORTFOLIO.type;
    dispatch({ type: actionType, portfolio: savedPortfolioWithPlanAccount });

    // Track newly created portfolios for highlighting in the UI
    if (portfolioIsNew(portfolio) && savedPortfolioWithPlanAccount._id) {
      dispatch({ type: ADD_NEWLY_CREATED_PORTFOLIO_ID.type, portfolioId: savedPortfolioWithPlanAccount._id });
    }

    return savedPortfolioWithPlanAccount;
  };

export const resetEditContact = (): Result<void> => dispatch => {
  dispatch(RESET_EDIT_CONTACT);
};

export const initLoadingPortfolios = (): Result<void> => dispatch => {
  dispatch(INIT_LOADING_PORTFOLIOS);
};

export const initLoadingComparisons = (): Result<void> => dispatch => {
  dispatch(INIT_LOADING_COMPARISONS);
};

export const sendPostMessageGetContactsAndPortfolios = (): Result<void> => (dispatch, getState) => {
  const { auth } = getState();
  PostMessageSender.getContactsAndPortfolios(dispatch, getState, auth.user);
};

export const saveContactRiskProfile =
  (profile: Partial<ContactRiskProfile>): AsyncResult<ContactRiskProfile[]> =>
  (dispatch, getState) => {
    dispatch(startLoaderAction(SET_IS_SAVING_CONTACT_RISK_PROFILE.type));

    const state = getState();
    const {
      contacts,
      intl: { locale },
    } = state;

    if (!contacts.current || !profile) {
      return Promise.reject();
    }

    const params = {
      contactId: contacts.current.contact._id,
      profileId: profile._id,
    };

    if (profile._id) {
      params.profileId = profile._id;

      return api
        .put(UPDATE_CONTACT_RISK_PROFILE, params, profile)
        .then((res: { data: any }) => res.data)
        .finally(() => dispatch(stopLoaderAction(SET_IS_SAVING_CONTACT_RISK_PROFILE.type)));
    }

    // This is necessary because the profile name is a localized string in the database
    profile.name = generateLocalizedStringFromText(profile.name?.[locale] || '');

    return api
      .post(SAVE_CONTACT_RISK_PROFILE, params, profile)
      .then((res: { data: any }) => res.data)
      .finally(() => dispatch(stopLoaderAction(SET_IS_SAVING_CONTACT_RISK_PROFILE.type)));
  };

export const deleteContactRiskProfile =
  (profile: ContactRiskProfile): AsyncResult<void> =>
  (dispatch, getState) => {
    const { contacts } = getState();

    if (!contacts.current || !profile._id) {
      return Promise.reject();
    }

    return api.delete(DELETE_CONTACT_RISK_PROFILE, { contactId: contacts.current.contact._id, profileId: profile._id });
  };

export const setCurrentContact =
  (contact: GenericContact): Result<void> =>
  dispatch => {
    dispatch({ type: SET_SELECTED_CONTACT.type, contact });
  };

export const acceptContactPortfolio =
  (contactId: string, portfolioId: string): AsyncResult<void> =>
  dispatch => {
    dispatch(startLoaderAction(ACCEPT_PORTFOLIO.type, { _id: portfolioId }));

    return api.post(ACCEPT_PORTFOLIO, { id: contactId, portfolioId }).finally(() => {
      dispatch(stopLoaderAction(ACCEPT_PORTFOLIO.type, { _id: portfolioId }));
    });
  };

export const setPersistenceSource =
  (user: User, key: string, value?: string): Result<void> =>
  (dispatch, getState) => {
    const { current } = getState().contacts;
    switch (key) {
      case localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE: {
        dispatch(RESET_NEXT_CONTACT_TOKEN);
        break;
      }
      case localStorageUtils.STORAGE_KEYS.CONTACT_PORTFOLIO_PERSISTENCE_SOURCE: {
        if (current != null) {
          dispatch(RESET_NEXT_CURRENT_CONTACT_PORTFOLIOS_TOKEN);
        }
        break;
      }
    }

    localStorageUtils.setPersistenceSource(user, key, value);
  };

export const updateEmbeddedContactComparison =
  (comparison: Comparison): Result<void> =>
  dispatch => {
    dispatch({ type: UPDATE_CONTACT_COMPARISON.type, comparison });
  };

export const clearNewlyCreatedPortfolioIds = (): Result<void> => dispatch => {
  dispatch({ type: CLEAR_NEWLY_CREATED_PORTFOLIO_IDS.type });
};
